/**
 * useYearDataAvailability Hook
 *
 * Checks data availability across multiple years for the Pro Calculator.
 * Helps users understand which years have data available and suggests
 * the most suitable year for calculations.
 *
 * Data sources checked:
 * - Hourly consumption data (from Tauron import)
 * - Basic calculator saved calculations
 * - Pro calculator saved calculations
 */

import { useState, useEffect, useCallback } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import { getAmplifyClient } from '../../../lib/amplifyClient';
import { getSavedCalculationByYear } from '../../../lib/api/savedCalculationApi';
import { getSavedProCalculationByYear } from '../../../lib/api/savedProCalculationApi';

/**
 * Data availability status for a single year
 */
export interface YearDataStatus {
  year: number;
  hasHourlyData: boolean;
  hourlyDataMonths: number; // 0-12, how many months have hourly data
  hasSavedCalculation: boolean; // Basic calculator
  hasSavedProCalculation: boolean; // Pro calculator
  isLoading: boolean;
  error: string | null;
}

/**
 * Return type for the hook
 */
export interface UseYearDataAvailabilityResult {
  yearStatuses: Map<number, YearDataStatus>;
  isLoading: boolean;
  error: string | null;
  suggestedYear: number | null;
  suggestedYearReason: string | null;
  refresh: () => void;
}

/**
 * Get available years to check (current + previous 2)
 */
export function getAvailableYears(): number[] {
  const currentYear = new Date().getFullYear();
  return [currentYear - 2, currentYear - 1, currentYear];
}

/**
 * Check if user has hourly data for a specific year and count months
 */
async function checkHourlyDataForYear(year: number): Promise<{ hasData: boolean; monthCount: number }> {
  try {
    const client = getAmplifyClient();
    const inputJson = JSON.stringify({ year });

    const result = await client.queries.getImportSummary({
      input: inputJson,
    });

    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message);
    }

    if (!result.data) {
      return { hasData: false, monthCount: 0 };
    }

    // Parse AWSJSON response
    let parsedResult: {
      success: boolean;
      totalMonths: number;
      imports?: Array<{ yearMonth: string }>;
    };

    if (typeof result.data === 'string') {
      parsedResult = JSON.parse(result.data);
      // Handle double-stringified response
      if (typeof parsedResult === 'string') {
        parsedResult = JSON.parse(parsedResult);
      }
    } else {
      parsedResult = result.data as typeof parsedResult;
    }

    const monthCount = parsedResult.totalMonths || 0;
    return {
      hasData: monthCount > 0,
      monthCount,
    };
  } catch {
    // User may not have any data, or may not be authenticated
    return { hasData: false, monthCount: 0 };
  }
}

/**
 * Check if user has a saved basic calculation for a specific year
 */
async function checkSavedCalculationForYear(
  userId: string,
  year: number
): Promise<boolean> {
  try {
    const calculation = await getSavedCalculationByYear(userId, year);
    return calculation !== null;
  } catch {
    return false;
  }
}

/**
 * Check if user has a saved pro calculation for a specific year
 */
async function checkSavedProCalculationForYear(
  userId: string,
  year: number
): Promise<boolean> {
  try {
    const calculation = await getSavedProCalculationByYear(userId, year);
    return calculation !== null;
  } catch {
    return false;
  }
}

/**
 * Determine the suggested year based on data availability
 *
 * Priority:
 * 1. Year with hourly data (most months)
 * 2. Year with saved basic calculation
 * 3. Previous year (most likely to have complete data)
 */
function determineSuggestedYear(
  statuses: Map<number, YearDataStatus>
): { year: number | null; reason: string | null } {
  const years = getAvailableYears().sort((a, b) => b - a); // Newest first
  let bestYear: number | null = null;
  let bestReason: string | null = null;
  let bestHourlyMonths = 0;

  for (const year of years) {
    const status = statuses.get(year);
    if (!status || status.isLoading) continue;

    // Priority 1: Year with most hourly data
    if (status.hasHourlyData && status.hourlyDataMonths > bestHourlyMonths) {
      bestYear = year;
      bestHourlyMonths = status.hourlyDataMonths;
      bestReason =
        status.hourlyDataMonths >= 12
          ? `Dane godzinowe dostępne dla całego roku ${year}`
          : `Dane godzinowe dostępne dla ${status.hourlyDataMonths} miesięcy w roku ${year}`;
    }
  }

  // Priority 2: If no hourly data, check for saved calculations
  if (bestYear === null) {
    for (const year of years) {
      const status = statuses.get(year);
      if (!status || status.isLoading) continue;

      if (status.hasSavedCalculation) {
        bestYear = year;
        bestReason = `Zapisana kalkulacja podstawowa dla roku ${year}`;
        break;
      }
    }
  }

  // Priority 3: Default to previous year (most likely to have complete data)
  if (bestYear === null) {
    const previousYear = new Date().getFullYear() - 1;
    bestYear = previousYear;
    bestReason = `Rok ${previousYear} - poprzedni pełny rok kalendarzowy`;
  }

  return { year: bestYear, reason: bestReason };
}

/**
 * Hook to check data availability across multiple years
 *
 * @param yearsToCheck - Optional array of years to check. Defaults to current + previous 2 years.
 * @returns Object with year statuses, loading state, suggested year, and refresh function
 *
 * @example
 * ```tsx
 * const { yearStatuses, suggestedYear, suggestedYearReason, isLoading } = useYearDataAvailability();
 *
 * // Check specific year
 * const status2025 = yearStatuses.get(2025);
 * if (status2025?.hasHourlyData) {
 *   console.log(`2025 has ${status2025.hourlyDataMonths} months of data`);
 * }
 *
 * // Use suggested year
 * if (suggestedYear) {
 *   setCalculationYear(suggestedYear);
 * }
 * ```
 */
export function useYearDataAvailability(
  yearsToCheck?: number[]
): UseYearDataAvailabilityResult {
  const years = yearsToCheck || getAvailableYears();

  const [yearStatuses, setYearStatuses] = useState<Map<number, YearDataStatus>>(
    () => {
      const initialMap = new Map<number, YearDataStatus>();
      for (const year of years) {
        initialMap.set(year, {
          year,
          hasHourlyData: false,
          hourlyDataMonths: 0,
          hasSavedCalculation: false,
          hasSavedProCalculation: false,
          isLoading: true,
          error: null,
        });
      }
      return initialMap;
    }
  );

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suggestedYear, setSuggestedYear] = useState<number | null>(null);
  const [suggestedYearReason, setSuggestedYearReason] = useState<string | null>(null);

  const checkAllYears = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    // Initialize all years as loading
    setYearStatuses((prev) => {
      const newMap = new Map(prev);
      for (const year of years) {
        const existing = newMap.get(year);
        if (existing) {
          newMap.set(year, { ...existing, isLoading: true, error: null });
        }
      }
      return newMap;
    });

    try {
      // Get current user ID (if authenticated)
      let userId: string | null = null;
      try {
        const user = await getCurrentUser();
        userId = user.userId;
      } catch {
        // User not authenticated - only check hourly data (which requires auth anyway)
        // For unauthenticated users, all data checks will return false
      }

      // Check all years in parallel for each data type
      const results = await Promise.all(
        years.map(async (year) => {
          const [hourlyResult, hasSaved, hasSavedPro] = await Promise.all([
            checkHourlyDataForYear(year),
            userId ? checkSavedCalculationForYear(userId, year) : Promise.resolve(false),
            userId ? checkSavedProCalculationForYear(userId, year) : Promise.resolve(false),
          ]);

          return {
            year,
            hasHourlyData: hourlyResult.hasData,
            hourlyDataMonths: hourlyResult.monthCount,
            hasSavedCalculation: hasSaved,
            hasSavedProCalculation: hasSavedPro,
            isLoading: false,
            error: null,
          } as YearDataStatus;
        })
      );

      // Update state with all results
      const newStatuses = new Map<number, YearDataStatus>();
      for (const result of results) {
        newStatuses.set(result.year, result);
      }
      setYearStatuses(newStatuses);

      // Determine suggested year
      const suggestion = determineSuggestedYear(newStatuses);
      setSuggestedYear(suggestion.year);
      setSuggestedYearReason(suggestion.reason);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Błąd podczas sprawdzania dostępności danych';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [years.join(',')]); // Re-run if years array changes

  // Initial check on mount
  useEffect(() => {
    void checkAllYears();
  }, [checkAllYears]);

  return {
    yearStatuses,
    isLoading,
    error,
    suggestedYear,
    suggestedYearReason,
    refresh: checkAllYears,
  };
}

/**
 * Get a human-readable label for year data status
 */
export function getYearStatusLabel(status: YearDataStatus | undefined): string {
  if (!status) return '';
  if (status.isLoading) return 'Sprawdzanie...';

  const parts: string[] = [];

  if (status.hasHourlyData) {
    if (status.hourlyDataMonths >= 12) {
      parts.push('Pełne dane godzinowe');
    } else {
      parts.push(`${status.hourlyDataMonths}/12 miesięcy`);
    }
  }

  if (status.hasSavedCalculation) {
    parts.push('Kalkulacja podstawowa');
  }

  if (status.hasSavedProCalculation) {
    parts.push('Kalkulacja PRO');
  }

  if (parts.length === 0) {
    return 'Brak danych';
  }

  return parts.join(' • ');
}

/**
 * Get badge color based on data availability
 */
export function getYearStatusColor(
  status: YearDataStatus | undefined
): 'green' | 'yellow' | 'gray' | 'blue' {
  if (!status || status.isLoading) return 'gray';

  // Green: Has complete hourly data (12 months)
  if (status.hasHourlyData && status.hourlyDataMonths >= 12) {
    return 'green';
  }

  // Yellow: Has partial hourly data
  if (status.hasHourlyData && status.hourlyDataMonths > 0) {
    return 'yellow';
  }

  // Blue: Has saved calculation but no hourly data
  if (status.hasSavedCalculation || status.hasSavedProCalculation) {
    return 'blue';
  }

  // Gray: No data
  return 'gray';
}
